import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, updateDoc, where } from 'firebase/firestore';
import { db, storage } from '../firebaseConfig';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';

export type ChallengePayload = {
  title: string;
  imageUrl?: string;
  description?: string;
  startAt: number;
  endAt: number;
  status: 'scheduled'|'active'|'ended';
  requiredCount: number;
  pixReward: number;
  requireUniqueTasks?: boolean;
  targetLanguage?: string;
  eligible?: {
    categories?: string[];
    subcategories?: string[];
    taskIds?: string[];
  };
  i18n?: { title?: Record<string, string>; description?: Record<string, string> };
};

export async function listChallenges() {
  try {
    // Try with composite index first, fallback to simple query if index doesn't exist
    const q = query(collection(db, 'challenges'), orderBy('status'), orderBy('endAt'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err: any) {
    // If composite index error, fallback to simple query
    if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
      console.warn('Composite index not found, using simple query. Please create index for (status, endAt)');
      const q = query(collection(db, 'challenges'));
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort in memory
      return items.sort((a: any, b: any) => {
        if (a.status !== b.status) return a.status.localeCompare(b.status);
        const aEnd = a.endAt?.toMillis ? a.endAt.toMillis() : a.endAt;
        const bEnd = b.endAt?.toMillis ? b.endAt.toMillis() : b.endAt;
        return aEnd - bEnd;
      });
    }
    throw err;
  }
}

export async function getChallenge(id: string) {
  const d = await getDoc(doc(db, 'challenges', id));
  return d.exists() ? { id: d.id, ...d.data() } : null;
}

export async function createChallenge(payload: ChallengePayload, file?: File) {
  const refDoc = doc(collection(db, 'challenges'));
  let imageUrl = payload.imageUrl || '';
  if (file) {
    const imgRef = ref(storage, `challenges/${refDoc.id}/image`);
    await uploadBytes(imgRef, file);
    imageUrl = await getDownloadURL(imgRef);
  }
  await setDoc(refDoc, {
    ...payload,
    imageUrl,
    analytics: { participantsCount: 0, completionsCount: 0, pixPaidTotal: 0 },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return refDoc.id;
}

export async function updateChallenge(id: string, payload: Partial<ChallengePayload>, file?: File) {
  let updates: any = { ...payload, updatedAt: serverTimestamp() };
  if (file) {
    const imgRef = ref(storage, `challenges/${id}/image`);
    await uploadBytes(imgRef, file);
    updates.imageUrl = await getDownloadURL(imgRef);
  }
  await updateDoc(doc(db, 'challenges', id), updates);
}

export async function deleteChallenge(id: string) {
  await deleteDoc(doc(db, 'challenges', id));
}

